import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/** Mirrors OnboardingState in src/Platform.Domain/Enums.cs. */
export const OnboardingState = {
  Created: 0,
  Consented: 1,
  ConnectorsCreated: 2,
  Pilot: 3,
  Validated: 4,
  OrgWide: 5,
  Offboarding: 6,
} as const;

export type OnboardingStateValue = (typeof OnboardingState)[keyof typeof OnboardingState];

export const onboardingStateLabels: Record<number, string> = {
  0: 'Created',
  1: 'Consented',
  2: 'Connectors created',
  3: 'Pilot',
  4: 'Validated',
  5: 'Org-wide',
  6: 'Offboarding',
};

/** i18n keys under the `tenants` namespace (see OnboardingBadge). */
export const onboardingStateLabelKeys: Record<number, string> = {
  0: 'onboardingStateCreated',
  1: 'onboardingStateConsented',
  2: 'onboardingStateConnectors',
  3: 'onboardingStatePilot',
  4: 'onboardingStateValidated',
  5: 'onboardingStateOrgWide',
  6: 'onboardingStateOffboarding',
};

/** Mirrors StampingMode. Active means we rewrite messages; Bypass relays them untouched. */
export const StampingMode = {
  Active: 1,
  Bypass: 2,
} as const;

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  onboardingState: number;
  stampingMode: number;
  snapshotVersion: number;
  smartHost: string | null;
  verifiedDomains: string[];
  bypassedAt: string | null;
  bypassReasonNote: string | null;
}

export interface StampingSecretResult {
  secret: string;
  snapshotVersion: number;
}

export function useTenants() {
  return useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<TenantSummary[]>('/api/tenants'),
  });
}

export function useTenant(tenantId: string) {
  return useQuery({
    queryKey: ['tenants', tenantId],
    queryFn: () => api.get<TenantSummary>(`/api/tenants/${tenantId}`),
    enabled: tenantId.length > 0,
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; slug: string }) =>
      api.post<TenantSummary>('/api/tenants', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      // Partner grants expand from the book; refresh so the new customer appears in nav.
      void queryClient.invalidateQueries({ queryKey: ['session'] });
    },
  });
}

/**
 * Soft-deletes a tenant (platform owner only). Removes it from lists and stamping; the row is kept
 * for audit and billing history.
 */
export function useDeleteTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tenantId: string) => {
      await api.delete<void>(`/api/tenants/${tenantId}`);
    },
    onSuccess: (_data, tenantId) => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.removeQueries({ queryKey: ['tenants', tenantId] });
    },
  });
}

/**
 * The kill switch. A reason note is required rather than optional: the next person to look at a
 * bypassed tenant needs to know whether it was a deliberate operator action or a tripped breaker,
 * and "who turned this off and why" is the first question asked during an incident.
 */
export function useSetStampingMode(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { mode: number; note: string }) => {
      await api.post<void>(`/api/tenants/${tenantId}/stamping-mode`, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['tenants', tenantId] });
    },
  });
}

/** Forces mail-flow nodes to reload this tenant's snapshot. MSP-only. */
export function useBumpSnapshot(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ snapshotVersion: number }>(`/api/tenants/${tenantId}/snapshot/bump`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['tenants', tenantId] });
    },
  });
}

/** Where a tenant's pre-rendered signatures stand. */
export interface RenderStatus {
  /** A pass still owes this tenant work. */
  pending: boolean;
  /** A node is rendering it right now. */
  inFlight: boolean;
  requestedAt: string | null;
  lastCompletedAt: string | null;
  lastReason: string | null;
  lastError: string | null;
  /** Consecutive failures. Non-zero with an error means the tenant is backing off. */
  attempts: number;
}

/**
 * Polls while a render is outstanding and goes quiet once it settles.
 *
 * A publish returns as soon as the template compiles; the signatures themselves are rendered by a
 * worker moments later. Without this the screen would claim the work was done while several
 * thousand mailboxes still carried the previous signature.
 */
export function useRenderStatus(tenantId: string) {
  return useQuery({
    queryKey: ['render-status', tenantId],
    queryFn: (): Promise<RenderStatus> => api.get<RenderStatus>(`/api/tenants/${tenantId}/render`),
    enabled: tenantId.length > 0,
    refetchInterval: (query) => {
      const status = query.state.data;
      return status?.pending || status?.inFlight ? 2000 : false;
    },
  });
}

/** Queues a full re-render of this tenant's signatures. Accepted, not awaited. */
export function useRequestRender(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (): Promise<RenderStatus> => api.post<RenderStatus>(`/api/tenants/${tenantId}/render`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['render-status', tenantId] });
    },
  });
}

/**
 * Issues a new stamping auth secret. Returned once — only the hash is stored after this response.
 * MSP-only; the operator must paste it into the tenant's transport rule immediately.
 */
export function useRotateStampingSecret(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<StampingSecretResult>(`/api/tenants/${tenantId}/stamping-secret`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['tenants', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['mailflow', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['onboarding', tenantId] });
    },
  });
}

export interface OutlookSidebarSettings {
  sidebarDefaultEnabled: boolean;
  allowSelfServiceDefault: boolean;
  editableFields: string[];
  manifestUrl: string;
}

export function useOutlookSidebarSettings(tenantId: string) {
  return useQuery({
    queryKey: ['outlook-sidebar', tenantId],
    queryFn: () =>
      api.get<OutlookSidebarSettings>(`/api/tenants/${tenantId}/outlook-sidebar`),
    enabled: tenantId.length > 0,
  });
}

export function useSaveOutlookSidebarSettings(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sidebarDefaultEnabled: boolean;
      allowSelfServiceDefault: boolean;
      editableFields: string[];
    }) =>
      api.put<OutlookSidebarSettings>(`/api/tenants/${tenantId}/outlook-sidebar`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['outlook-sidebar', tenantId] });
    },
  });
}

export interface TenantDefaultFields {
  fills: Record<string, string | null>;
}

export function useTenantDefaultFields(tenantId: string) {
  return useQuery({
    queryKey: ['default-fields', tenantId],
    queryFn: () =>
      api.get<TenantDefaultFields>(`/api/tenants/${tenantId}/default-fields`),
    enabled: tenantId.length > 0,
  });
}

export function useSaveTenantDefaultFields(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { fills: Record<string, string | null> }) =>
      api.put<TenantDefaultFields>(`/api/tenants/${tenantId}/default-fields`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['default-fields', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['directory-users', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['user-detail', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['render-status', tenantId] });
    },
  });
}
